/** @jsxImportSource @emotion/react */

import { Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { createSearchParams, useLocation, useSearchParams } from 'react-router-dom'
import TabsNavigation from 'components/TabsNavigation/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import { cmsRoutes, RouteValue, useLink } from 'routes/cmsRoutes'
import { CMSBoosterPages, FaqPages, PlatformName, ServicePageTypeEnum } from '__generatedTypes__/globalTypes'
import { usePlatformsAndSubjects } from 'hooks/usePlatformsAndSubjects'
import { PageLoader } from 'components/PageLoader'
import FAQ from '../FAQ'
import Testimonials from '../Testimonials'
import { DentalSchoolStatistics } from '../BlogPages/DentalSchoolStatistics'
import { AdmissionServices } from './Services'
import * as classes from './styles'
import { AdmissionExperts } from './Experts/AdmissionExperts'
import { ADMISSION_DENTISTRY, ADMISSION_OPTOMETRY, ADMISSION_TYPES } from './consts'

export type AdmissionType = keyof typeof ADMISSION_TYPES

const SERVICES_TAB = 'Services'
const ADMISSION_EXPERTS_TAB = 'AdmissionExperts'
const SUCCESS_STORIES_TAB = 'SuccessStories'
const FAQ_TAB = 'FAQ'
export const SCHOOL_STATISTICS_TAB = 'SchoolStatistics'

const COMMON_TABS: Array<{ id: string; title: string }> = [
  {
    id: SERVICES_TAB,
    title: 'Services',
  },
  {
    id: ADMISSION_EXPERTS_TAB,
    title: 'Admission Experts',
  },
  {
    id: SUCCESS_STORIES_TAB,
    title: 'Success Stories',
  },
  {
    id: FAQ_TAB,
    title: 'FAQ',
  },
]

const getTabs = (type: AdmissionType) => [
  ...COMMON_TABS,
  {
    id: SCHOOL_STATISTICS_TAB,
    title: `${type === ADMISSION_DENTISTRY ? 'Dental' : 'Optometry'} School Statistics`,
  },
]

export const QUERY_PARAM = 'tab'

export type AdmissionManagementProps = {
  type: AdmissionType
}

export const AdmissionManagement = ({ type }: AdmissionManagementProps) => {
  const { selectedPlatform } = usePlatformsAndSubjects({})
  const location = useLocation()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()

  const [activeTab, setActiveTab] = useState<TabsItem>(() => {
    const tabId = searchParams.get(QUERY_PARAM)
    const availableTabs = getTabs(type)

    return availableTabs.find(({ id }) => id === tabId) || availableTabs[0]
  })

  useEffect(() => {
    if (selectedPlatform && selectedPlatform.title !== PlatformName.Admissions) {
      navigate(`/${String(selectedPlatform.slug)}/${cmsRoutes.NOT_FOUND}`)
    }
  }, [selectedPlatform?.title, type])

  const handleChangeTab = (activeTab: TabsItem) => {
    navigate(location.pathname as RouteValue, undefined, {
      searchParams: createSearchParams({ [QUERY_PARAM]: activeTab.id }),
    })
    setActiveTab(activeTab)
  }

  const renderTab = () => {
    if (!selectedPlatform) {
      return <></>
    }

    switch (activeTab.id) {
      case SERVICES_TAB:
        return (
          <AdmissionServices
            platform={selectedPlatform}
            page={type === ADMISSION_DENTISTRY ? ServicePageTypeEnum.Dentistry : ServicePageTypeEnum.Optometry}
          />
        )
      case ADMISSION_EXPERTS_TAB:
        return (
          <AdmissionExperts
            platform={selectedPlatform}
            page={type === ADMISSION_DENTISTRY ? ServicePageTypeEnum.Dentistry : ServicePageTypeEnum.Optometry}
          />
        )
      case SUCCESS_STORIES_TAB:
        return (
          <Testimonials
            platform={selectedPlatform}
            fixedPage={type === ADMISSION_DENTISTRY ? CMSBoosterPages.Dentistry : CMSBoosterPages.Optometry}
          />
        )
      case FAQ_TAB:
        return <FAQ page={type === ADMISSION_DENTISTRY ? FaqPages.Dentistry : FaqPages.Optometry} />

      case SCHOOL_STATISTICS_TAB:
        return <DentalSchoolStatistics isOptometry={type === ADMISSION_OPTOMETRY} />
    }
    return <></>
  }

  if (!selectedPlatform) {
    return <PageLoader />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        {ADMISSION_TYPES[type]} Management
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={getTabs(type)} value={activeTab} onChange={handleChangeTab} />

      {renderTab()}
    </section>
  )
}
